import { useState, createContext, useContext, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Sparkles,
  Users,
  UserCircle,
  BarChart3,
  FileText,
  FileSignature,
  Settings,
  Moon,
  Sun,
  LogOut,
  Building2,
  Shield,
  CreditCard,
  ChevronRight,
  Bot,
  Megaphone,
  Mail,
} from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { useAuth } from "@/lib/auth-context";
import { useBranding } from "@/lib/branding-context";

// ════════════════════════════════════════════════════════════════════════════
// Sidebar Context - Shares expanded state with content areas
// ════════════════════════════════════════════════════════════════════════════

interface SidebarContextType {
  isExpanded: boolean;
  isPinned: boolean;
  sidebarWidth: number;
}

const SidebarContext = createContext<SidebarContextType>({
  isExpanded: false,
  isPinned: false,
  sidebarWidth: 64,
});

export function useSidebar() {
  return useContext(SidebarContext);
}

// ════════════════════════════════════════════════════════════════════════════
// Sidebar Component
// ════════════════════════════════════════════════════════════════════════════
// Glassmorphic sidebar with hover expansion and pin toggle.
// Collapsed: 64px icon rail | Expanded: 220px with labels
// Content pushes fluidly when sidebar expands

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  roles?: string[];
}

const navItems: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  {
    icon: Sparkles,
    label: "Services",
    path: "/services",
    roles: ["owner", "admin", "manager"],
  },
  {
    icon: Users,
    label: "Providers",
    path: "/providers",
    roles: ["owner", "admin", "manager"],
  },
  { icon: UserCircle, label: "Clients", path: "/clients" },
  {
    icon: CreditCard,
    label: "Payments",
    path: "/payments",
    roles: ["owner", "admin", "manager"],
  },
  {
    icon: BarChart3,
    label: "Analytics",
    path: "/analytics",
    roles: ["owner", "admin"],
  },
  {
    icon: FileText,
    label: "Forms",
    path: "/forms",
    roles: ["owner", "admin", "manager"],
  },
  {
    icon: FileSignature,
    label: "Documents",
    path: "/documents",
    roles: ["owner", "admin", "manager"],
  },
  {
    icon: Bot,
    label: "AI Chatbot",
    path: "/chatbot",
    roles: ["owner", "admin"],
  },
  {
    icon: Megaphone,
    label: "Campaigns",
    path: "/campaigns",
    roles: ["owner", "admin", "manager", "staff"],
  },
  { icon: Settings, label: "Settings", path: "/settings", roles: ["owner", "admin"] },
];

// Platform admin navigation items (only shown for super admins)
const adminNavItems: NavItem[] = [
  { icon: Shield, label: "Admin Panel", path: "/admin/dashboard" },
  { icon: Building2, label: "Tenants", path: "/admin/tenants" },
  { icon: Users, label: "Platform Users", path: "/admin/users" },
  { icon: Megaphone, label: "Meta Leads", path: "/admin/meta-leads" },
  { icon: Bot, label: "Chatbot Usage", path: "/admin/chatbot" },
];

const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 220;

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isExpanded = isPinned || isHovered;
  const sidebarWidth = isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  return (
    <SidebarContext.Provider value={{ isExpanded, isPinned, sidebarWidth }}>
      <SidebarInner
        isExpanded={isExpanded}
        isPinned={isPinned}
        setIsPinned={setIsPinned}
        setIsHovered={setIsHovered}
      />
      {/* Main content wrapper that adjusts with sidebar */}
      <motion.div
        className="min-h-screen"
        initial={false}
        animate={{ marginLeft: sidebarWidth }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      >
        {children}
      </motion.div>
    </SidebarContext.Provider>
  );
}

interface SidebarInnerProps {
  isExpanded: boolean;
  isPinned: boolean;
  setIsPinned: (pinned: boolean) => void;
  setIsHovered: (hovered: boolean) => void;
}

function SidebarInner({ isExpanded, isPinned, setIsPinned, setIsHovered }: SidebarInnerProps) {
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { getCacheBustedLogoUrl } = useBranding();
  const logoUrl = getCacheBustedLogoUrl();
  const [campaignsExpanded, setCampaignsExpanded] = useState(
    location.pathname.startsWith("/campaigns")
  );

  // Filter nav items based on user role
  const visibleNavItems = navItems.filter((item) => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  // Check if user is a platform admin
  const isPlatformAdmin = user?.isPlatformUser && user?.role === "super_admin";

  return (
    <motion.aside
      className="fixed left-0 top-0 h-screen glass-surface flex flex-col z-50"
      initial={false}
      animate={{ width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label="Main navigation"
      role="navigation"
    >
      {/* Logo Area */}
      <div className="h-16 flex items-center px-3 border-b border-white/10">
        <div className="flex items-center">
          {logoUrl ? (
            // Custom business logo (with cache buster for fresh uploads)
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 overflow-hidden p-1">
              <img
                src={logoUrl}
                alt="Business logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  console.error("[Sidebar Logo] Failed to load:", logoUrl);
                  // Hide the broken image and let the fallback show
                  (e.target as HTMLImageElement).style.display = "none";
                }}
                crossOrigin="anonymous"
              />
            </div>
          ) : (
            // Default AF logo
            <div className="w-10 h-10 rounded-lg bg-af-900 dark:bg-af-100 flex items-center justify-center flex-shrink-0">
              <span className="text-white dark:text-af-900 font-bold text-lg">AF</span>
            </div>
          )}
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, delay: 0.1 }}
                className="ml-3 font-semibold text-af-900 dark:text-af-100 whitespace-nowrap"
              >
                Appointment Flo
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-hide" aria-label="Primary">
        {/* Pin/Expand Toggle - Always visible, stacked with nav icons */}
        <div className="px-2 mb-2">
          <button
            onClick={() => setIsPinned(!isPinned)}
            className={`
              flex items-center w-full h-10 px-2 rounded-lg transition-all duration-200
              ${isPinned
                ? "bg-af-500/20"
                : "hover:bg-white/10 dark:hover:bg-white/5"
              }
            `}
            title={isPinned ? "Unpin sidebar" : "Pin sidebar open"}
          >
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <motion.div
                animate={{ rotate: isPinned ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronRight
                  size={20}
                  className={isPinned ? "text-af-900 dark:text-af-100" : "text-af-700 dark:text-af-300"}
                />
              </motion.div>
            </div>
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, delay: 0.1 }}
                  className={`
                    ml-3 text-sm font-medium whitespace-nowrap
                    ${isPinned ? "text-af-900 dark:text-af-100" : "text-af-700 dark:text-af-300"}
                  `}
                >
                  {isPinned ? "Collapse Sidebar" : "Pin Sidebar"}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
        {/* Platform Admin Section */}
        {isPlatformAdmin && (
          <>
            <div className="px-2 mb-2">
              <AnimatePresence>
                {isExpanded && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, delay: 0.1 }}
                    className="text-xs font-semibold text-af-500 uppercase tracking-wider"
                  >
                    Platform Admin
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            <ul className="space-y-1 px-2 mb-4">
              {adminNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`
                        flex items-center h-10 px-2 rounded-lg transition-all duration-200
                        ${
                          isActive
                            ? "bg-amber-500/20 border-l-3 border-amber-500"
                            : "hover:bg-white/10 dark:hover:bg-white/5"
                        }
                      `}
                    >
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <Icon
                          size={20}
                          className={
                            isActive
                              ? "text-amber-500"
                              : "text-amber-600 dark:text-amber-400"
                          }
                        />
                      </div>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15, delay: 0.1 }}
                            className={`
                              ml-3 text-sm font-medium whitespace-nowrap
                              ${
                                isActive
                                  ? "text-amber-500"
                                  : "text-amber-600 dark:text-amber-400"
                              }
                            `}
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-white/10 mb-4" />
          </>
        )}

        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            const Icon = item.icon;
            const isCampaigns = item.path === "/campaigns";

            if (isCampaigns) {
              const isEmailBlastsActive = location.pathname.startsWith("/campaigns/email-blasts");
              const isCampaignsActive = location.pathname === "/campaigns" && !isEmailBlastsActive;
              return (
                <li key={item.path}>
                  {/* Campaigns toggle button */}
                  <button
                    onClick={() => setCampaignsExpanded((v) => !v)}
                    className={`
                      flex items-center w-full h-10 px-2 rounded-lg transition-all duration-200
                      ${
                        isActive
                          ? "bg-white/15 dark:bg-white/10 border-l-3 border-af-500"
                          : "hover:bg-white/10 dark:hover:bg-white/5"
                      }
                    `}
                  >
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <Icon
                        size={20}
                        className={isActive ? "text-af-900 dark:text-af-100" : "text-af-700 dark:text-af-300"}
                      />
                    </div>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15, delay: 0.1 }}
                          className={`ml-3 text-sm font-medium whitespace-nowrap flex-1 text-left ${
                            isActive ? "text-af-900 dark:text-af-100" : "text-af-700 dark:text-af-300"
                          }`}
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          <ChevronRight
                            size={14}
                            className={`transition-transform duration-200 text-af-400 ${campaignsExpanded ? "rotate-90" : ""}`}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>

                  {/* Sub-items */}
                  <AnimatePresence>
                    {campaignsExpanded && isExpanded && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <li>
                          <Link
                            to="/campaigns"
                            className={`flex items-center h-9 pl-8 pr-2 rounded-lg text-xs transition-all duration-200 ${
                              isCampaignsActive
                                ? "text-af-900 dark:text-af-100 font-medium bg-white/10"
                                : "text-af-500 dark:text-af-400 hover:text-af-900 dark:hover:text-af-100 hover:bg-white/5"
                            }`}
                          >
                            Campaigns
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/campaigns/email-blasts"
                            className={`flex items-center gap-2 h-9 pl-8 pr-2 rounded-lg text-xs transition-all duration-200 ${
                              isEmailBlastsActive
                                ? "text-af-900 dark:text-af-100 font-medium bg-white/10"
                                : "text-af-500 dark:text-af-400 hover:text-af-900 dark:hover:text-af-100 hover:bg-white/5"
                            }`}
                          >
                            <Mail size={12} />
                            Email Blasts
                          </Link>
                        </li>
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            }

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`
                    flex items-center h-10 px-2 rounded-lg transition-all duration-200
                    ${
                      isActive
                        ? "bg-white/15 dark:bg-white/10 border-l-3 border-af-500"
                        : "hover:bg-white/10 dark:hover:bg-white/5"
                    }
                  `}
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <Icon
                      size={20}
                      className={
                        isActive
                          ? "text-af-900 dark:text-af-100"
                          : "text-af-700 dark:text-af-300"
                      }
                    />
                  </div>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, delay: 0.1 }}
                        className={`
                          ml-3 text-sm font-medium whitespace-nowrap
                          ${
                            isActive
                              ? "text-af-900 dark:text-af-100"
                              : "text-af-700 dark:text-af-300"
                          }
                        `}
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-white/10 py-4 px-2 space-y-1">
        {/* Dark Mode Toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center w-full h-10 px-2 rounded-lg hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
        >
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            {resolvedTheme === "dark" ? (
              <Sun size={20} className="text-af-300" />
            ) : (
              <Moon size={20} className="text-af-700" />
            )}
          </div>
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, delay: 0.1 }}
                className="ml-3 text-sm font-medium text-af-700 dark:text-af-300 whitespace-nowrap"
              >
                {resolvedTheme === "dark" ? "Light Mode" : "Dark Mode"}
              </motion.span>
            )}
          </AnimatePresence>
        </button>

        {/* User Info */}
        {user && (
          <div className="flex items-center h-10 px-2">
            <div className="w-6 h-6 rounded-full bg-af-500 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-white">
                {user.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
              </span>
            </div>
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, delay: 0.1 }}
                  className="ml-3 flex-1 min-w-0"
                >
                  <p className="text-sm font-medium text-af-900 dark:text-af-100 truncate">
                    {user.name || user.email}
                  </p>
                  <p className="text-xs text-af-500 capitalize">{user.role}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={logout}
          className="flex items-center w-full h-10 px-2 rounded-lg hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
        >
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <LogOut size={20} className="text-af-700 dark:text-af-300" />
          </div>
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, delay: 0.1 }}
                className="ml-3 text-sm font-medium text-af-700 dark:text-af-300 whitespace-nowrap"
              >
                Sign Out
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );
}

// Legacy export for backwards compatibility
export function Sidebar() {
  return null; // This is now handled by SidebarProvider
}

export default Sidebar;
